// pages/HabitacionesVenta.js
import React, { useState, useEffect, useRef } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Switch,
  Select,
  Space,
  Typography,
  message,
  Tag,
  Tooltip,
  Row,
  Col,
  Popconfirm,
  Badge,
  Statistic,
  Spin,
  Card
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  HomeOutlined,
  ExportOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined
} from '@ant-design/icons';
import axios from 'axios';

import PageContainer from '../components/PageContainer';
import { BASE_URL } from '../utils/constants';

const { Title, Text } = Typography;
const { Option } = Select;

const HabitacionesVenta = () => {
  // Estados
  const [habitaciones, setHabitaciones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState('create');
  const [currentHabitacion, setCurrentHabitacion] = useState(null);
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);

  // Estados para filtros y ordenación
  const [filteredInfo, setFilteredInfo] = useState({});
  const [sortedInfo, setSortedInfo] = useState({});

  // Estado para paginación
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} registros`
  });

  // Configuración de columnas de la tabla
  const columns = [
    {
      title: 'Código',
      dataIndex: 'Codigo',
      key: 'Codigo',
      sorter: (a, b) => a.Codigo.localeCompare(b.Codigo),
      sortOrder: sortedInfo.columnKey === 'Codigo' && sortedInfo.order,
      width: 120,
      render: (codigo) => (
        <Tag color="blue" style={{ fontWeight: 'bold', fontSize: '13px' }}>
          {codigo}
        </Tag>
      ),
    },
    {
      title: 'Descripción',
      dataIndex: 'Descripcion',
      key: 'Descripcion',
      sorter: (a, b) => {
        const aVal = a.Descripcion || '';
        const bVal = b.Descripcion || '';
        return aVal.localeCompare(bVal);
      },
      sortOrder: sortedInfo.columnKey === 'Descripcion' && sortedInfo.order,
      ellipsis: true,
      width: 250,
    },
    {
      title: 'Actualizar Channel',
      dataIndex: 'ActualizarChannel',
      key: 'ActualizarChannel',
      render: (ActualizarChannel) => (
        <Badge
          status={ActualizarChannel ? 'success' : 'error'}
          text={ActualizarChannel ? 'Se actualiza' : ''}
        />
      ),
      width: 100,

      onFilter: (value, record) => record.ActualizarChannel === value,
    },
    {
      title: 'Estado',
      dataIndex: 'Activo',
      key: 'Activo',
      render: (activo) => (
        <Badge
          status={activo ? 'success' : 'error'}
          text={activo ? 'Activa' : 'Inactiva'}
        />
      ),
      width: 100,
      filters: [
        { text: 'Activas', value: true },
        { text: 'Inactivas', value: false },
      ],
      filteredValue: filteredInfo.Activo || null,
      onFilter: (value, record) => record.Activo === value,
    },    
    {
      title: 'Por Defecto',
      dataIndex: 'porDefecto',
      key: 'porDefecto',
      render: (porDefecto) => (
        porDefecto ? (
          <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '18px' }} />
        ) : (
          <CloseCircleOutlined style={{ color: '#d9d9d9', fontSize: '18px' }} />
        )
      ),
      width: 100,
      filters: [
        { text: 'Sí', value: true },
        { text: 'No', value: false },
      ],
      filteredValue: filteredInfo.porDefecto || null,
      onFilter: (value, record) => record.porDefecto === value,
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 180,
      render: (_, record) => (
        <Space>
          <Tooltip title="Ver detalles">
            <Button
              type="default"
              icon={<EyeOutlined />}
              size="small"
              onClick={() => handleView(record)}
            />
          </Tooltip>
          
          <Tooltip title="Editar">
            <Button
              type="primary"
              icon={<EditOutlined />}
              size="small"
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          
          <Popconfirm
            title="¿Eliminar habitación?"
            description="Esta acción no se puede deshacer"
            onConfirm={() => handleDelete(record.Codigo)}
            okText="Sí"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Eliminar">
              <Button
                danger
                icon={<DeleteOutlined />}
                size="small"
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Cargar datos iniciales
  useEffect(() => {
    fetchHabitaciones();
  }, []);


  const fetchHabitaciones = async (params = {}) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Parámetros de la query
      const queryParams = {
        page: params.current || pagination.current,
        pageSize: params.pageSize || pagination.pageSize,
        search: searchText,
        sortField: params.sortField || sortedInfo.field || 'Descripcion',
        sortOrder: params.sortOrder || (sortedInfo.order === 'ascend' ? 'ASC' : 'DESC')
      };
      
      // Añadir filtros
      if (filteredInfo.Activo) {
        queryParams.Activo = filteredInfo.Activo[0];
      }
      if (filteredInfo.porDefecto) {
        queryParams.porDefecto = filteredInfo.porDefecto[0];
      }
      if (filteredInfo.CodigoHotel) {
        queryParams.CodigoHotel = filteredInfo.CodigoHotel[0];
      }
      
      const response = await axios.get(`${BASE_URL}/api/habitacionesventa`, {
        headers: { Authorization: `Bearer ${token}` },
        params: queryParams
      });

      const cleanedData = (response.data.data || []).map(item => ({
        ...item,
        Descripcion: item.Descripcion || '',
        CodigoHotel: item.CodigoHotel || '',
        Activo: Boolean(item.Activo),
        porDefecto: Boolean(item.porDefecto)
      }));
      
      setHabitaciones(cleanedData);
      
      // Actualizar paginación
      setPagination({
        ...pagination,
        current: response.data.pagination?.currentPage || 1,
        pageSize: response.data.pagination?.pageSize || pagination.pageSize,
        total: response.data.pagination?.totalRecords || cleanedData.length
      });
      
    } catch (error) {
      console.error('❌ Error cargando habitaciones:', error);
      message.error('Error al cargar las habitaciones');
      setHabitaciones([]);
      setPagination(prev => ({ ...prev, total: 0 }));
    } finally {
      setLoading(false);
      setSearchLoading(false);
    }
  };

  const handleTableChange = (newPagination, filters, sorter) => {
    // Actualizar estados de filtros y ordenación
    setFilteredInfo(filters);
    setSortedInfo(sorter);
    
    // Construir parámetros para la nueva carga
    const params = {
      current: newPagination.current,
      pageSize: newPagination.pageSize,
      sortField: sorter.field,
      sortOrder: sorter.order === 'ascend' ? 'ASC' : 'DESC'
    };
    
    // Actualizar paginación
    setPagination(prev => ({
      ...prev,
      current: newPagination.current,
      pageSize: newPagination.pageSize
    }));
    
    // Cargar datos con nuevos parámetros
    fetchHabitaciones(params);
  };

  // Handlers para las acciones
  const handleCreate = async () => {
    setModalType('create');
    setCurrentHabitacion(null);
    form.resetFields();
    
    // Establecer valores por defecto
    form.setFieldsValue({
      Activo: true,
      porDefecto: false,
    });
    
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setModalType('edit');
    setCurrentHabitacion(record);
    
    const formValues = {
      Codigo: record.Codigo,
      Descripcion: record.Descripcion || '',
      CodigoHotel: record.CodigoHotel || '',
      Activo: record.Activo,
      porDefecto: record.porDefecto,
    };
    
    form.setFieldsValue(formValues);
    setModalVisible(true);
  };

  const handleView = (record) => {
    
    Modal.info({
      title: `Detalle de Habitación: ${record.Descripcion}`,
      width: 600,
      content: (
        <div style={{ marginTop: 20 }}>
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <strong>Código:</strong>
              <div style={{ marginTop: 4 }}>
                <Tag color="blue">{record.Codigo}</Tag>
              </div>
            </Col>
            <Col span={12}>
              <strong>Estado:</strong>
              <div style={{ marginTop: 4 }}>
                <Badge
                  status={record.Activo ? 'success' : 'error'}
                  text={record.Activo ? 'Activa' : 'Inactiva'}
                />
              </div>
            </Col>
            <Col span={12}>
              <strong>Se actualiza:</strong>
              <div style={{ marginTop: 4 }}>
                <Badge
                  status={record.ActualizarChannel ? 'success' : 'error'}
                  text={record.ActualizarChannel ? 'Se actualiza' : ''}
                />
              </div>
            </Col>            
            
            <Col span={24}>
              <strong>Descripción:</strong>
              <div style={{ marginTop: 4, fontSize: '16px', fontWeight: '500' }}>
                {record.Descripcion || 'Sin descripción'}
              </div>
            </Col>
            
            <Col span={12}>
              <strong>Hotel:</strong>
              <div style={{ marginTop: 4 }}>
                {hotel ? (
                  <>
                    <HomeOutlined style={{ marginRight: 4, color: '#52c41a' }} />
                    {hotel.Descripcion || hotel.Nombre || `Hotel ${record.CodigoHotel}`}
                    <div style={{ fontSize: '12px', color: '#666', marginTop: 2 }}>
                      Código: {record.CodigoHotel}
                    </div>
                  </>
                ) : (
                  <span style={{ color: '#999' }}>Hotel {record.CodigoHotel}</span>
                )}
              </div>
            </Col>
            
            <Col span={12}>
              <strong>Por Defecto:</strong>
              <div style={{ marginTop: 4 }}>
                {record.porDefecto ? (
                  <>
                    <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />
                    Sí
                  </>
                ) : (
                  <>
                    <CloseCircleOutlined style={{ color: '#d9d9d9', marginRight: 8 }} />
                    No
                  </>
                )}
              </div>
            </Col>
          </Row>
        </div>
      ),
      onOk() {},
    });
  };

  const handleDelete = async (codigo) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${BASE_URL}/api/habitacionesventa/${codigo}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Filtrar el eliminado
      setHabitaciones(habitaciones.filter(h => h.Codigo !== codigo));
      
      // Actualizar total
      setPagination(prev => ({
        ...prev,
        total: prev.total - 1
      }));
      
      message.success('Habitación eliminada correctamente');
    } catch (error) {
      console.error('❌ Error eliminando habitación:', error);
      message.error(error.response?.data?.error || 'Error al eliminar la habitación');
    }
  };

  // Handler para guardar (crear o editar)
    const handleSave = async () => {
    try {
        const values = await form.validateFields();
        const token = localStorage.getItem('token');

        if (modalType === 'create') {
        const response = await axios.post(
            `${BASE_URL}/api/habitacionesventa`,
            values,
            { headers: { Authorization: `Bearer ${token}` } }
        );
        // Recargar los datos para asegurar que todo está actualizado
        fetchHabitaciones();
        message.success('Habitación creada correctamente');
        } else {
        const response = await axios.put(
            `${BASE_URL}/api/habitacionesventa/${currentHabitacion.Codigo}`,
            values,
            { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Actualizar el estado local con los datos que vienen del servidor
        // Asegurar que todos los campos necesarios están presentes
        const habitacionActualizada = {
            ...response.data.data, // Usar data.data si la respuesta tiene esa estructura
            Codigo: currentHabitacion.Codigo, // Mantener el código original
            Descripcion: values.Descripcion,
            CodigoHotel: values.CodigoHotel,
            Activo: values.Activo,
            porDefecto: values.porDefecto
        };
        
        // Buscar y actualizar el registro específico
        setHabitaciones(prevHabitaciones => 
            prevHabitaciones.map(habitacion => 
            habitacion.Codigo === currentHabitacion.Codigo 
                ? habitacionActualizada 
                : habitacion
            )
        );
        
        message.success('Habitación actualizada correctamente');
        }
        
        setModalVisible(false);
        form.resetFields();
        
    } catch (error) {
        console.error('❌ Error guardando habitación:', error);
        message.error(error.response?.data?.error || 'Error al guardar los datos');
    }
    };

  // Funciones de búsqueda
  const handleSearch = () => {
    if (searchText.trim() === '') {
      message.warning('Por favor ingrese un término de búsqueda');
      return;
    }
    setSearchLoading(true);
    // Reiniciar a página 1 cuando se busca
    setPagination({ ...pagination, current: 1 });
    fetchHabitaciones({ current: 1 });
  };

  const handleResetSearch = () => {
    setSearchText('');
    setFilteredInfo({});
    setSortedInfo({});
    setSearchLoading(false);
    setPagination({ ...pagination, current: 1 });
    fetchHabitaciones({ current: 1 });
  };

  const searchBar = (
    <div style={{ marginBottom: 16 }}>
      <Space>
        <Input
          placeholder="Buscar por código, descripción..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: 350 }}
          onPressEnter={handleSearch}
          allowClear
          onClear={handleResetSearch}
        />
        <Button
          type="primary"
          icon={<SearchOutlined />}
          onClick={handleSearch}
          loading={searchLoading}
        >
          Buscar
        </Button>
        <Button
          icon={<ReloadOutlined />}
          onClick={handleResetSearch}
        >
          Restablecer
        </Button>
        <span style={{ color: '#666', fontSize: '12px' }}>
          {pagination.total.toLocaleString()} registros
        </span>
      </Space>
    </div>
  );

  // Función para exportar (placeholder)
  const handleExport = () => {
    message.info('Función de exportación en desarrollo');
  };

  // Estadísticas
  const estadisticas = (
    <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
      <Col span={6}>
        <Statistic 
          title="Total Habitaciones" 
          value={pagination.total}
          prefix={<HomeOutlined />}
          valueStyle={{ color: '#1890ff' }}
        />
      </Col>
      <Col span={6}>
        <Statistic 
          title="Activas" 
          value={habitaciones.filter(h => h.Activo).length}
          valueStyle={{ color: '#52c41a' }}
        />
      </Col>
      <Col span={6}>
        <Statistic 
          title="Por Defecto" 
          value={habitaciones.filter(h => h.porDefecto).length}
          valueStyle={{ color: '#faad14' }}
        />
      </Col>
      <Col span={6}>
        <Statistic 
          title="Página" 
          value={pagination.current}
          suffix={`/ ${Math.ceil(pagination.total / pagination.pageSize)}`}
          valueStyle={{ color: '#722ed1' }}
        />
      </Col>
    </Row>
  );

  return (
    <>
      <PageContainer
        title="Habitaciones de Venta"
        subtitle={`${pagination.total.toLocaleString()} habitaciones registradas`}
        icon={<HomeOutlined />}
        loading={loading}
        onRefresh={() => fetchHabitaciones()}
        onCreate={handleCreate}
        badgeCount={pagination.total}
        badgeStatus="processing"
        extra={
          <Space>
            <Button
              icon={<ExportOutlined />}
              onClick={handleExport}
            >
              Exportar
            </Button>
            <Tag color="blue" style={{ fontSize: 14, padding: '4px 12px', borderRadius: 20 }}>
              <strong>{pagination.total.toLocaleString()}</strong> registros
            </Tag>
          </Space>
        }
        cardStyle={{
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
        }}
      >
        {/* Estadísticas */}
        {estadisticas}
        
        {/* Barra de búsqueda */}
        {searchBar}
        
        {/* Tabla */}
        <Table
          columns={columns}
          dataSource={habitaciones}
          rowKey="Codigo"
          loading={loading}
          onChange={handleTableChange}
          pagination={pagination}
          scroll={{ x: 1200, y: 600 }}
          style={{
            borderRadius: '8px',
            overflow: 'hidden'
          }}
          size="middle"
          locale={{
            emptyText: 'No se encontraron habitaciones'
          }}
        />
      </PageContainer>

      {/* Modal para crear/editar habitación */}
      <Modal
        title={modalType === 'create' ? 'Nueva Habitación de Venta' : 'Editar Habitación'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        width={600}
        okText={modalType === 'create' ? 'Crear' : 'Guardar'}
        cancelText="Cancelar"
        confirmLoading={loading}
        style={{ top: 20 }}
        bodyStyle={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            Activo: true,
            porDefecto: false,
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="Codigo"
                label="Código"
                rules={[{ 
                  required: true, 
                  message: 'Por favor ingrese el código',
                  max: 10,
                  pattern: /^[A-Z0-9]+$/,
                  message: 'Solo letras mayúsculas y números, máximo 10 caracteres'
                }]}
              >
                <Input 
                  placeholder="Ej: DB, DBT, FST" 
                  maxLength={10}
                  disabled={modalType === 'edit'}
                  style={{ textTransform: 'uppercase' }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="Descripcion"
                label="Descripción"
                rules={[{ 
                  required: true, 
                  message: 'Por favor ingrese la descripción',
                  max: 100
                }]}
              >
                <Input.TextArea 
                  placeholder="Ej: DOBLE STANDARD CON TERRAZA, FAMILIAR VISTA MAR..." 
                  rows={3}
                  maxLength={100}
                  showCount
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="ActualizarChannel"
                label="Se actualiza"
                valuePropName="checked"
              >
                <Switch
                  checkedChildren="Sí"
                  unCheckedChildren="No"
                />
              </Form.Item>
            </Col>            
            <Col span={12}>
              <Form.Item
                name="Activo"
                label="Activa"
                valuePropName="checked"
              >
                <Switch
                  checkedChildren="Sí"
                  unCheckedChildren="No"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="porDefecto"
                label="Por Defecto"
                valuePropName="checked"
              >
                <Switch
                  checkedChildren="Sí"
                  unCheckedChildren="No"
                />
              </Form.Item>
            </Col>
          </Row>

          {modalType === 'create' && (
            <Card size="small" style={{ marginTop: 16, backgroundColor: '#f6ffed' }}>
              <div style={{ fontSize: '13px', color: '#666' }}>
                <strong>📝 Nota:</strong> El código debe ser único y normalmente sigue un patrón:
                <ul style={{ marginTop: 8, marginBottom: 8, paddingLeft: 20 }}>
                  <li>DB = Doble Básica</li>
                  <li>DST = Doble Standard con Terraza</li>
                  <li>FST = Familiar Standard con Terraza</li>
                  <li>Etc.</li>
                </ul>
                Consulte con el administrador del sistema para el formato correcto.
              </div>
            </Card>
          )}
        </Form>
      </Modal>
    </>
  );
};

export default HabitacionesVenta;